import { useAuth } from '../contexts/AuthContext';
import { generateClient } from 'aws-amplify/api';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { DataGrid } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import '../App.css';
import awsConfig from '../aws-config';

const GET_VRM_DASHBOARD_DATA = `
  query GetDashboardData {
    getDashboardData {
      isused
      instancestate
      customerid
      startdateutc
      enddateutc
      usecase
      language
      requester
      version
      loadbalancerarn
      environment
      url
      cfstackname
      servicenowticketid
      application
      projectname
    }
  }
`;

// Custom header renderer with filter dropdown
const ApplicationHeaderRenderer = ({ column, filters, onFilterChange }) => {
  return (
    <div className="header-with-filter">
      <div className="header-text">{column.name}</div>
      <select
        className="header-filter-select"
        value={filters.application || ''}
        onChange={(e) => onFilterChange('application', e.target.value)}
        onClick={(e) => e.stopPropagation()} // Prevent column sorting when clicking dropdown
      >
        <option value="">All Applications</option>
        <option value="DataManagementSoftware">DataManagementSoftware</option>
        <option value="VrmTraining">VrmTraining</option>
        <option value="unknown">unknown</option>
      </select>
    </div>
  );
};
const ProjectnameHeaderRenderer = ({ column, filters, onFilterChange }) => {
  return (
    <div className="header-with-filter">
      <div className="header-text">{column.name}</div>
      <select
        className="header-filter-select"
        value={filters.projectname || ''}
        onChange={(e) => onFilterChange('projectname', e.target.value)}
        onClick={(e) => e.stopPropagation()} // Prevent column sorting when clicking dropdown
      >
        <option value="">All Projects</option>
        <option value="Byg4Lab">Byg4Lab</option>
        <option value="OrthoConnect">OrthoConnect</option>
        <option value="unknown">unknown</option>
      </select>
    </div>
  );
};
const IsUsedHeaderRenderer = ({ column, filters, onFilterChange }) => {
  return (
    <div className="header-with-filter">
      <div className="header-text">{column.name}</div>
      <select
        className="header-filter-select"
        value={filters.isused || ''}
        onChange={(e) => onFilterChange('isused', e.target.value)}
        onClick={(e) => e.stopPropagation()} // Prevent column sorting when clicking dropdown
      >
        <option value="">All States</option>
        <option value="true">True</option>
        <option value="false">False</option>
      </select>
    </div>
  );
};
const InstanceStateHeaderRenderer = ({ column, filters, onFilterChange }) => {
  return (
    <div className="header-with-filter">
      <div className="header-text">{column.name}</div>
      <select
        className="header-filter-select"
        value={filters.instancestate || ''}
        onChange={(e) => onFilterChange('instancestate', e.target.value)}
        onClick={(e) => e.stopPropagation()} // Prevent column sorting when clicking dropdown
      >
        <option value="">All States</option>
        <option value="running">Running</option>
        <option value="terminated">Terminated</option>
        <option value="stopped">Stopped</option>
        <option value="unknown">Unknown</option>
      </select>
    </div>
  );
};

// Custom cell renderers
const DateCellRenderer = ({ row, column }) => {
  const value = row[column.key];
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return value;
  }
};

const URLCellRenderer = ({ row, column }) => {
  const value = row[column.key];
  return (
    <div style={{ 
      color: '#1976d2', 
      wordBreak: 'break-all',
      whiteSpace: 'normal',
      lineHeight: '1.4'
    }} title={value}>
      {value}
    </div>
  );
};

const TextCellRenderer = ({ row, column }) => {
  const value = row[column.key];
  return (
    <div style={{ 
      wordBreak: 'break-word',
      whiteSpace: 'normal',
      lineHeight: '1.4',
      padding: '8px 0'
    }} title={value}>
      {value}
    </div>
  );
};

const LongTextCellRenderer = ({ row, column }) => {
  const value = row[column.key];
  return (
    <div style={{ 
      wordBreak: 'break-all',
      whiteSpace: 'normal',
      lineHeight: '1.4',
      padding: '8px 0'
    }} title={value}>
      {value}
    </div>
  );
};

const UserProfile = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiKeyLoaded, setApiKeyLoaded] = useState(false);
  const [client, setClient] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [filters, setFilters] = useState({
    isused: 'true'  // Default to 'running'
  });

  // ADD THESE NEW STATE VARIABLES FOR SORTING
  const [sortColumns, setSortColumns] = useState([]);
  // ADD THIS SORTING FUNCTION

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return filteredRows;

    return [...filteredRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const { columnKey, direction } = sort;
        let aValue = a[columnKey];
        let bValue = b[columnKey];

        // Handle null/undefined values
        if (aValue == null && bValue == null) continue;
        if (aValue == null) return direction === 'ASC' ? -1 : 1;
        if (bValue == null) return direction === 'ASC' ? 1 : -1;

        // Convert to string for comparison if not already
        if (typeof aValue !== 'string' && typeof aValue !== 'number') {
          aValue = String(aValue);
          bValue = String(bValue);
        }

        // Handle date columns specifically
        if (columnKey === 'startdateutc' || columnKey === 'enddateutc') {
          aValue = new Date(aValue).getTime();
          bValue = new Date(bValue).getTime();
        }

        // Perform comparison
        let comparisonResult = 0;
        if (typeof aValue === 'string') {
          comparisonResult = aValue.localeCompare(bValue, undefined, { 
            numeric: true, 
            sensitivity: 'base' 
          });
        } else {
          comparisonResult = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        }

        if (comparisonResult !== 0) {
          return direction === 'ASC' ? comparisonResult : -comparisonResult;
        }
      }
      return 0;
    });
  }, [filteredRows, sortColumns]);

  // Handle filter changes
  const handleFilterChange = (column, value) => {
    setFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  // Column definitions for react-data-grid
  const columns = useMemo(() => [
    {
      key: 'isused',
      name: 'In Use',
      width: 150,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      renderHeaderCell: (props) => (
        <IsUsedHeaderRenderer 
          {...props} 
          filters={filters} 
          onFilterChange={handleFilterChange} 
        />
      ),
      headerCellClass: 'custom-header-cell-with-filter'
    },
    {
      key: 'instancestate',
      name: 'Instance State',
      width: 150,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      renderHeaderCell: (props) => (
        <InstanceStateHeaderRenderer 
          {...props} 
          filters={filters} 
          onFilterChange={handleFilterChange} 
        />
      ),
      headerCellClass: 'custom-header-cell-with-filter'
    },
    {
      key: 'projectname',
      name: 'Project Name',
      width: 150,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      renderHeaderCell: (props) => (
        <ProjectnameHeaderRenderer
          {...props} 
          filters={filters} 
          onFilterChange={handleFilterChange} 
        />
      ),
      headerCellClass: 'custom-header-cell-with-filter'
    },
    {
      key: 'application',
      name: 'Application',
      width: 150,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      renderHeaderCell: (props) => (
        <ApplicationHeaderRenderer
          {...props} 
          filters={filters} 
          onFilterChange={handleFilterChange} 
        />
      ),
      headerCellClass: 'custom-header-cell-with-filter'
    },
    {
      key: 'customerid',
      name: 'Customer ID',
      width: 150,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'startdateutc',
      name: 'Start Date UTC',
      width: 180,
      resizable: true,
      sortable: true,
      renderCell: DateCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'enddateutc',
      name: 'End Date UTC',
      width: 180,
      resizable: true,
      sortable: true,
      renderCell: DateCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'usecase',
      name: 'Use Case',
      width: 200,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'language',
      name: 'Language',
      width: 120,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'requester',
      name: 'Requester',
      width: 150,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'version',
      name: 'Version',
      width: 120,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'loadbalancerarn',
      name: 'Load Balancer ARN',
      width: 300,
      resizable: true,
      sortable: true,
      renderCell: LongTextCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'environment',
      name: 'Environment',
      width: 150,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'url',
      name: 'URL',
      width: 250,
      resizable: true,
      sortable: true,
      renderCell: URLCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'cfstackname',
      name: 'CF Stack Name',
      width: 180,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      headerCellClass: 'custom-header-cell'
    },
    {
      key: 'servicenowticketid',
      name: 'Ticket ID',
      width: 150,
      resizable: true,
      sortable: true,
      renderCell: TextCellRenderer,
      headerCellClass: 'custom-header-cell'
    }
  ], [filters]);

  const initializeAmplify = async () => {
    try {
      setLoading(true);
      
      const apiClient = generateClient();
      setClient(apiClient);
      setApiKeyLoaded(true);
      
      console.log("Amplify configured successfully");
    } catch (err) {
      console.error('Error initializing Amplify:', err);
      setError(`Failed to initialize: ${err.message}`);
      setLoading(false);
    }
  };

  const fetchData = async () => {
    if (!client) {
      setError('API client not initialized');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log("Attempting to fetch dashboard data from AppSync...");
      
      const response = await client.graphql({
        query: GET_VRM_DASHBOARD_DATA
      });
      
      console.log("Response received:", response);
      
      const dashboardData = response.data.getDashboardData;
      console.log("Dashboard Data extracted:", dashboardData);
      
      const formattedData = Array.isArray(dashboardData) ? dashboardData : [dashboardData];
      
      const dataWithIds = formattedData.map((item, index) => ({
        id: index,
        ...item
      }));
      
      console.log("Formatted data for grid:", dataWithIds);
      
      setRows(dataWithIds);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to fetch dashboard data. Please check console for more details.');
      setLoading(false);
    }
  };

  const loadSampleData = () => {
    const sampleData = [
      { 
        id: 1, 
        instancestate: "running", 
        customerid: "ACME123", 
        startdateutc: "2023-07-15T10:00:00", 
        enddateutc: "2023-08-15T10:00:00",
        usecase: "Demo Environment for Testing Long Text Content",
        language: "English",
        requester: "John Doe",
        version: "v1.2.3",
        loadbalancerarn: "arn:aws:elasticloadbalancing:us-west-2:123456789012:loadbalancer/app/my-very-long-load-balancer-name-that-should-wrap/1234567890123456",
        environment: "Development",
        url: "https://very-long-development-environment-url-example.com/path/to/resource",
        cfstackname: "dev-stack-01-with-long-name",
        servicenowticketid: "TICKET-12345-LONG-ID"
      },
      { 
        id: 2, 
        instancestate: "stopped", 
        customerid: "XYZ456", 
        startdateutc: "2023-06-20T08:30:00", 
        enddateutc: "2023-07-20T08:30:00",
        usecase: "Production Environment with Extended Description for Multiple Use Cases",
        language: "Spanish",
        requester: "Jane Smith",
        version: "v1.1.0",
        loadbalancerarn: "arn:aws:elasticloadbalancing:us-west-2:123456789012:loadbalancer/app/production-load-balancer-with-very-descriptive-name/6543210987654321",
        environment: "Production",
        url: "https://production-environment-with-long-domain-name.example.com/api/v1/resources",
        cfstackname: "prod-stack-02-comprehensive",
        servicenowticketid: "PROD-TICKET-67890"
      },
      { 
        id: 3, 
        instancestate: "terminated", 
        customerid: "TEST789", 
        startdateutc: "2023-08-01T12:00:00", 
        enddateutc: "2023-09-01T12:00:00",
        usecase: "Testing and Quality Assurance Environment for Comprehensive System Validation",
        language: "French",
        requester: "Bob Johnson",
        version: "v2.0.0-beta.1",
        loadbalancerarn: "arn:aws:elasticloadbalancing:eu-west-1:123456789012:loadbalancer/app/testing-environment-load-balancer-comprehensive-name/9876543210987654",
        environment: "Testing & QA",
        url: "https://testing-quality-assurance-environment.example.org/testing/comprehensive/path",
        cfstackname: "test-stack-03-full-featured",
        servicenowticketid: "TEST-QA-TICKET-11111"
      }
    ];
    setRows(sampleData);
    setError(null);
    setLoading(false);
  };

  // Filter function
  const applyFilters = (data, filters) => {
    return data.filter(row => {
      for (const [key, value] of Object.entries(filters)) {
        if (value && row[key]) {
          const cellValue = row[key].toString().toLowerCase();
          const filterValue = value.toString().toLowerCase();
          if (!cellValue.includes(filterValue)) {
            return false;
          }
        }
      }
      return true;
    });
  };

  // Apply filters whenever rows or filters change
  useEffect(() => {
    const filtered = applyFilters(rows, filters);
    setFilteredRows(filtered);
  }, [rows, filters]);

  // Initialize Amplify on component mount
  useEffect(() => {
    if (user) {
      initializeAmplify();
    }
  }, [user]);

  // Fetch data when API client is ready
  useEffect(() => {
    if (user && apiKeyLoaded && client) {
      fetchData();
    }
  }, [user, apiKeyLoaded, client]);

  if (!user) return null;

  return (
    <div className="App">
      <header className="App-header">
        <h1>VRM Dashboard</h1>
      </header>
      
      <main style={{ maxWidth: '100%', overflow: 'hidden', padding: '20px' }}>
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>
              {!apiKeyLoaded 
                ? "Initializing API client..." 
                : "Loading dashboard data from API..."
              }
            </p>
          </div>
        )}
        
        {error && (
          <div className="error-container">
            <p>Error: {error}</p>
            <button 
              className="sample-button" 
              onClick={loadSampleData}
            >
              Load Sample Data Instead
            </button>
          </div>
        )}
        
        {!loading && !error && rows.length === 0 && apiKeyLoaded && (
          <div className="warning-container">
            <p>No dashboard data was returned from the API.</p>
            <button 
              className="sample-button" 
              onClick={loadSampleData}
            >
              Load Sample Data
            </button>
          </div>
        )}
        
        {rows.length > 0 && (
  <div className="grid-container">
    <DataGrid
      columns={columns}
      rows={sortedRows} // Changed from filteredRows to sortedRows
      selectedRows={selectedRows}
      onSelectedRowsChange={setSelectedRows}
      sortColumns={sortColumns} // Add this prop
      onSortColumnsChange={setSortColumns} // Add this prop
      className="rdg-light custom-data-grid"
      style={{ height: '75vh' }}
      rowHeight={60}
      headerRowHeight={70}
    />
  </div>
)}

        {rows.length > 0 && (
          <div className="data-actions">
            <button 
              className="action-button refresh" 
              onClick={() => {
                setLoading(true);
                setError(null);
                setRows([]);
                fetchData();
              }}
            >
              Refresh Data
            </button>
            {/* <div className="selection-info">
              {selectedRows.size > 0 && (
                <p>{selectedRows.size} row(s) selected</p>
              )}
            </div> */}
          </div>
        )}
      </main>
      
      <footer className="App-footer">
        <p>VRM Data Viewer</p>
      </footer>
    </div>
  );
};

export default UserProfile;